import DoneIcon from '@mui/icons-material/Done';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreIcon from '@mui/icons-material/Restore';
import CloseIcon from '@mui/icons-material/Close';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Alert, Box, Button, Card, CardContent, CardHeader, CircularProgress, Drawer, IconButton, Link, List, ListItem, ListItemIcon, ListItemText, Skeleton, Stack, 
  TextField, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { crudGetList, Identifier, ListContextProvider, RecordMap, ReduxState, useListContext, useListController } from "ra-core";
import { FC, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Content } from "~/components/content/Content";
import { RecPatch } from "~/dataProvider/tools";
import { ClientDto, ClientStatus } from "~/gen/api/user";
import { OdResourcePageProps } from "~/types/resource";
import { pages, sorts } from "~/utils/paramsWithGetMainList";
import { useResourceRegister } from "~/utils/resourceRegister";
import { crudUpdateEx } from '~/actions/crudFetchEx';
import { useDefaultSavedHandlers } from '~/controller/defaultSavedHandlers';
import Confirm from '~/components/widgets/Confirm';
import DataAnalyzerRegisterCode from '~/components/settings/clients/DataAnalyzerRegisterCodes';
import usePermitGuard from '~/authProvider/usePermitGuard';
import { MANAGE_SECUREFILE_SETTINGS } from '~/constants';



const analyzerRegisterCodeRes = 'data-analyzer-register-codes' 
const analyzerElectRes = 'data-analyzer-elect'

const OnpremisesDataAnalyzerSettings: FC<OdResourcePageProps> = ({options}) => {
  useResourceRegister({ name: analyzerRegisterCodeRes })
  useResourceRegister({ name: analyzerElectRes })

  usePermitGuard([[MANAGE_SECUREFILE_SETTINGS]])
  return (
    <Content pageName="Data Analyzer Configuration" resourceName="Data Analyzer Configuration" breadcrumbs={options?.breadcrumbs}>
      <ListContextProvider value={useListController({ basePath: '', sort: defaultSort})}>
        <OnpremisesDataAnalyzerSettingsBody />
      </ListContextProvider>
    </Content>
  )
}

const initVal = (ids: Identifier[], data: RecordMap<RecPatch>) => ({
  stage: (ids ?? []).reduce((acc, id) => ({...acc, [id]: (data[id] as any as ClientDto).serviceEndpoint!}), {} as Record<string, string>),
  errors: (ids ?? []).reduce((acc, id) => ({...acc, [id]: !urlRgx.test((data[id] as any as ClientDto).serviceEndpoint!)}), {} as Record<string, boolean>),
  touched: (ids ?? []).reduce((acc, id) => ({...acc, [id]: false}), {} as Record<string, boolean>),
  confirmRevoke: null as {c:ClientDto, revoke:()=>void}|null,
  openRegisterCodes: false,
})

const OnpremisesDataAnalyzerSettingsBody: FC = () => {
  const { ids, data, resource, refetch } = useListContext()
  const loadedOnce = useSelector<ReduxState, boolean>(store => !!store.admin.resources[resource].list.loadedOnce)
  
  const [states, setStates] = useState(initVal(ids, data))
  
  useEffect(() => {
    setStates(initVal(ids, data))
  }, [ids, data])

  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(crudGetList(analyzerRegisterCodeRes, pages(), sorts(), {}))
  }, [dispatch])

  const handlers = useDefaultSavedHandlers("Update Data Analyzer Config")
  handlers.onSuccess.merge(refetch)

  const saveAction = (c: ClientDto) => ({
    updateServiceUrl(s: string) {
      dispatch(crudUpdateEx(resource, c.clientId!, {dto:{ clientId: c.clientId, serviceEndpoint: s}}, c, handlers))
    },
    revoke() {
      dispatch(crudUpdateEx(resource, c.clientId!, {dto:{status: ClientStatus.Revoked}, disableAnalyzer:ids.length === 1}, c, handlers))
    },
    elect() {
      dispatch(crudUpdateEx(analyzerElectRes, c.clientId!, undefined, c, handlers))
    },
  })
  const match = useMediaQuery('(max-width: 1400px)')
  const width = match ? 'calc(100vw - 150px)' : 1250

  const loading = useSelector<ReduxState>(state => state.admin.loading > 0);
  return (
    <Card sx={{ mt: 5 }}>
      <CardHeader title="Data Analyzer Configuration" 
        action={
          <Button variant="contained" 
            onClick={() => setStates(v => ({...v, openRegisterCodes: true}))} >Registration Code</Button>} 
      />
      <CardContent>
        {!loadedOnce ? <Skeleton height={200} /> :
          <Alert severity="info" sx={{ mb: 1 }}>
            The Data Analyzer component is used to identify sensitive information in documents and files. 
            You may follow <Link component={RouterLink} to="/documents/how-to_guides/securefile/deploy_data_analyzer" underline="always">these instructions</Link> 
            {" "}to deploy one or more Data Analyzer instances in your own network environment, 
            so that the SecureFile clients can use them to identify sensitive data in documents and files.<br />
            <Button size="small" variant="text" onClick={refetch} sx={{mt: 1}} startIcon={
              loading ? <CircularProgress color="inherit" size={14} thickness={6} /> :<RefreshIcon />
            } >Refresh</Button>
          </Alert>}
        {(!ids || ids.length === 0) && 
          <Typography mt={2}>No registered data analyzers</Typography>
          }
        {ids && ids.length > 0 &&
          <List >
            {ids.map(id => {
              const c : ClientDto = data[id] as any
              const {updateServiceUrl, revoke, elect} = saveAction(c)
              return (
                <ListItem key={id} sx={{
                  '& button.elect-btn': {
                    '& .i1': { display: 'block' },
                    '& .i2': { display: 'none' },
                    '&:hover': {
                      '& .i1': { display: 'none' },
                      '& .i2': { display: 'block' },
                    }
                  }
                }}>
                  <ListItemIcon>
                    {c.status === ClientStatus.Enabled && 
                      <Tooltip title="Active">
                        <Box>
                          <CheckCircleIcon color={!!states.stage[id] ? 'success': undefined} />
                        </Box>
                      </Tooltip>}
                    {c.status === ClientStatus.Suspend && 
                      <Tooltip title="Activate" onClick={elect}>
                        <IconButton className="elect-btn" sx={{ p: 'unset' }} disabled={!states.stage[id]}>
                          <BlockIcon className="i1" color={!!states.stage[id] ? 'success': undefined} />
                          <ThumbUpAltIcon className="i2" color="primary" />
                        </IconButton>
                      </Tooltip>}
                  </ListItemIcon>
                  <ListItemText>
                    <TextField label="Data Analyzer Instance" variant="standard" fullWidth 
                      error={states.touched[id] && states.errors[id]}
                      helperText={states.touched[id] && states.errors[id] ? 'Format incorrect' : undefined}
                      value={states.stage[id] ?? ''} 
                      onChange={(e) => { setStates(v => ({...v, 
                        stage: {...v.stage, [id]: e.target.value}, 
                        errors: {...v.errors, [id]: !urlRgx.test(e.target.value)}, 
                        touched: {...v.touched, [id]: true}, 
                      })) }}
                      onKeyDown={(e)=> {
                        if (!states.errors[id] && (e.code === 'Enter' || e.code === 'NumpadEnter')) {
                          if (c.status === ClientStatus.Enabled && states.stage[id] !== c.serviceEndpoint) {
                            updateServiceUrl(states.stage[id] ?? '')
                          }
                          e.preventDefault()
                        }
                      }}
                    />
                  </ListItemText>
                  <Stack direction="row-reverse" ml={2} width={120}>
                    {c.serviceEndpoint !== states.stage[id] &&
                      <>
                        <Tooltip title="Discard Change">
                          <IconButton color="info" onClick={() => setStates(v => ({...v, 
                            stage: {...v.stage, [id]: c.serviceEndpoint ?? ''}, 
                            errors: {...v.errors, [id]: !urlRgx.test(c.serviceEndpoint!)}, 
                            touched: {...v.touched, [id]: false}, 
                          }))}>
                            <RestoreIcon  />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Save Change">
                          <IconButton color="primary" disabled={states.errors[id]}
                            onClick={() => updateServiceUrl(states.stage[id] ?? '')}>
                            <DoneIcon  />
                          </IconButton>
                        </Tooltip>
                      </>}
                    {c.status === ClientStatus.Suspend && c.serviceEndpoint === states.stage[id] &&
                      <Tooltip title="Remove">
                        <IconButton color="error" onClick={() => setStates(v => ({...v, confirmRevoke: {c, revoke}}))}>
                          <CloseIcon  />
                        </IconButton>
                      </Tooltip>}
                  </Stack>
                </ListItem>)
            })}
          </List>
        }
        <Confirm 
          isOpen={Boolean(states.confirmRevoke)}
          title={"Remove Data Analyzer"}
          onConfirm={() => {
            states.confirmRevoke?.revoke()
            setStates(v => ({...v, confirmRevoke: null}))
          }}
          onClose={() => setStates(v => ({...v, confirmRevoke: null}))}
          confirm="Confirm"
          cancel="Cancel"
          content={(
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ ml: 1 }}
                >
                  Please confirm whether to remove Data Analyzer
                  "{states.confirmRevoke?.c.serviceEndpoint}".
                </Typography>
              </Box>
            </Box>
          )}
        />
        <Drawer
          anchor="right"
          onClose={()=>{ setStates(v => ({...v, openRegisterCodes: false})) }}
          open={states.openRegisterCodes}
          sx={{ zIndex: theme => theme.zIndex.appBar - 5 }}
          PaperProps={{
            sx: {
              p: 2,
              width: width,
              bgcolor: 'background.default',
              backgroundImage: 'none',
            },          
          }}
        >
          <Box sx={{ pt: 6 }}>
            <DataAnalyzerRegisterCode {...({close: () => setStates(v => ({...v, openRegisterCodes: false}))} as any)} />
          </Box>
        </Drawer>
      </CardContent>
    </Card>
  )
}

export default OnpremisesDataAnalyzerSettings;

const defaultSort = {
  field: 'register_at',
  order: 'DESC'
}

const urlRgx = /((?:https?:\/\/)(?:[-a-z0-9]+\.)*[-a-z0-9]+.*)/i
